/**
 * Who did the thing (GDN-2779).
 *
 * Every admin timeline used to render actors its own way — two printed raw
 * hex, one resolved names from a map of the org's own members (so staff never
 * resolved). This is the one component they all use now.
 *
 * Two rules worth stating, because both are deliberate:
 *
 * 1. **An id that didn't resolve renders as itself.** No "Unknown user", no
 *    placeholder. A run of raw hex on screen is a signal that something
 *    upstream is emitting actor ids we can't resolve; a friendly label would
 *    launder that into looking fine.
 *
 * 2. **A delegate is always shown the same way** — a visible indicator plus a
 *    tooltip — whatever kind of actor it hangs off. Per-kind exceptions are
 *    how the surfaces drifted apart in the first place.
 *
 * The backend hands back `{type, id}` rather than a URL, so route shapes stay
 * on the client where the rest of them live.
 */
import { Link as RouterLink } from 'react-router-dom';
import { Box, Link, Tooltip, Typography } from '@mui/material';

import type { EventActorSchema } from '../store/api';
import { entityHomePath } from '../utils/recent-entities';

/** Where an actor chip goes when clicked, or null when there's nothing to open. */
export const actorPath = (actor: EventActorSchema | null | undefined): string | null => {
  if (!actor?.link) return null;
  // 'user' covers staff too — staff are ordinary user documents.
  return actor.link.type === 'user'
    ? entityHomePath({ kind: 'user', id: actor.link.id })
    : null;
};

/** The text for an actor: its name if we have one, otherwise the id itself. */
const actorLabel = (actor: EventActorSchema): string => actor.display_name ?? actor.id;

/** Unresolved ids are monospace — they read as identifiers, not as names. */
const labelSx = (actor: EventActorSchema) =>
  actor.display_name ? undefined : { fontFamily: 'monospace', fontSize: 12 };

const ActorText = ({
  actor,
  variant = 'body2',
}: {
  actor: EventActorSchema;
  variant?: 'body2' | 'caption';
}) => {
  const path = actorPath(actor);
  const label = actorLabel(actor);

  if (!path) {
    return (
      <Typography component="span" variant={variant} sx={labelSx(actor)}>
        {label}
      </Typography>
    );
  }
  return (
    <Link
      component={RouterLink}
      to={path}
      variant={variant}
      sx={labelSx(actor)}
      onClick={event => event.stopPropagation()}
    >
      {label}
    </Link>
  );
};

export const EventActor = ({
  actor,
  onBehalfOf,
  variant = 'body2',
}: {
  actor?: EventActorSchema | null;
  /** The human a system job or staff member acted for, when there is one. */
  onBehalfOf?: EventActorSchema | null;
  variant?: 'body2' | 'caption';
}) => {
  if (!actor) {
    return (
      <Typography component="span" variant={variant} color="text.secondary">
        —
      </Typography>
    );
  }

  if (!onBehalfOf) return <ActorText actor={actor} variant={variant} />;

  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.5 }}>
      <ActorText actor={actor} variant={variant} />
      <Tooltip
        // Left interactive (the default) so the delegate's link is reachable.
        title={
          <Box sx={{ py: 0.25 }}>
            <Typography variant="caption" sx={{ display: 'block', opacity: 0.8 }}>
              on behalf of
            </Typography>
            <ActorText actor={onBehalfOf} variant="caption" />
          </Box>
        }
      >
        {/* The indicator has to be discoverable without a hover, so it's a
            visible mark rather than a bare cursor change. */}
        <Typography
          component="span"
          variant={variant}
          color="text.secondary"
          tabIndex={0}
          aria-label={`on behalf of ${actorLabel(onBehalfOf)}`}
          sx={{ cursor: 'help', textDecoration: 'underline dotted', whiteSpace: 'nowrap' }}
        >
          ↪
        </Typography>
      </Tooltip>
    </Box>
  );
};
